import { useEffect, useState } from 'react';
import { api, send } from './api.js';
import { Notice } from './components.js';
import { t } from './i18n.js';

type Key = {
  id: string;
  name: string;
  scopes: string[];
  expiresAt: string;
  lastUsedAt?: string;
  revokedAt?: string;
};
type Hook = {
  id: string;
  url: string;
  events: string[];
  enabled: boolean;
  lastStatus?: string;
  attempts: number;
};
type Sso = {
  configured: boolean;
  tenantId: string;
  clientId: string;
  enabled: boolean;
  callbackUrl: string;
};
type SsoLink = { userId: string; name: string; email: string; linkedAt: string };
const scopes = ['directory.read', 'calendars.read'];
const events = [
  'directory.save',
  'directory.delete',
  'team-calendar.create',
  'team-event.save',
  'team-event.delete',
  'conversation.assign',
];

export function ProIntegrations({ oidcEnabled }: { oidcEnabled: boolean }) {
  const [keys, setKeys] = useState<Key[]>([]);
  const [hooks, setHooks] = useState<Hook[]>([]);
  const [name, setName] = useState('');
  const [keyScopes, setKeyScopes] = useState<string[]>(['directory.read']);
  const [url, setUrl] = useState('');
  const [hookEvents, setHookEvents] = useState<string[]>(['directory.save']);
  const [once, setOnce] = useState<{ label: string; secret: string }>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sso, setSso] = useState<Sso>();
  const [ssoLinks, setSsoLinks] = useState<SsoLink[]>([]);
  const [entraTenant, setEntraTenant] = useState('');
  const [entraClient, setEntraClient] = useState('');
  const [entraSecret, setEntraSecret] = useState('');
  const load = async () => {
    const [k, h, configuration, links] = await Promise.all([
      api<Key[]>('/integrations/keys'),
      api<Hook[]>('/integrations/webhooks'),
      api<Sso>('/sso/configuration'),
      api<SsoLink[]>('/sso/links'),
    ]);
    setKeys(k);
    setHooks(h);
    setSso(configuration);
    setSsoLinks(links);
  };
  useEffect(() => {
    void load().catch((e) => setError(e.message));
  }, []);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const choice = (values: string[], value: string, setter: (values: string[]) => void) =>
    setter(values.includes(value) ? values.filter((x) => x !== value) : [...values, value]);
  return (
    <div className="pro-card">
      <h3>{t('Intégrations Pro')}</h3>
      <p>
        {t(
          'Créez des accès en lecture pour vos outils métier et recevez les événements choisis. Les secrets ne sont affichés qu’une seule fois.',
        )}
      </p>
      {error && <Notice type="error">{error}</Notice>}
      {once && (
        <Notice>
          <strong>{once.label}</strong>
          <br />
          <code style={{ overflowWrap: 'anywhere' }}>{once.secret}</code>
          <br />
          {t('Copiez ce secret maintenant : il ne sera plus visible.')}
        </Notice>
      )}
      {oidcEnabled && (
        <>
          <h4>{t('Connexion Microsoft Entra ID')}</h4>
          <p>
            {t(
              'Enregistrez une application web dans Microsoft Entra ID, ajoutez l’URL de retour ci-dessous et activez la MFA ainsi que la revendication AMR dans les jetons ID. Chaque membre lie ensuite son compte depuis Sécurité et sessions.',
            )}
          </p>
          <p>
            <strong>{t('URL de retour :')}</strong> <code>{sso?.callbackUrl}</code>
          </p>
          <p>
            {sso?.configured
              ? sso.enabled
                ? t('SSO actif')
                : t('SSO configuré, à activer')
              : t('SSO non configuré')}
          </p>
          <form
            className="pro-form"
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                await send(
                  '/sso/configuration',
                  { tenantId: entraTenant, clientId: entraClient, clientSecret: entraSecret },
                  'PUT',
                );
                setEntraSecret('');
              });
            }}
          >
            <label>
              {t('ID du tenant Entra')}
              <input
                required
                value={entraTenant}
                onChange={(e) => setEntraTenant(e.target.value)}
              />
            </label>
            <label>
              {t('ID de l’application cliente')}
              <input
                required
                value={entraClient}
                onChange={(e) => setEntraClient(e.target.value)}
              />
            </label>
            <label>
              {t('Secret client')}
              <input
                required
                type="password"
                autoComplete="new-password"
                value={entraSecret}
                onChange={(e) => setEntraSecret(e.target.value)}
              />
            </label>
            <button disabled={busy} type="submit">
              {t('Enregistrer la configuration SSO')}
            </button>
          </form>
          {sso?.configured && (
            <button
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await send('/sso/status', { enabled: !sso.enabled }, 'PUT');
                })
              }
            >
              {sso.enabled ? t('Désactiver le SSO') : t('Activer le SSO')}
            </button>
          )}
          {ssoLinks.length > 0 && (
            <div>
              <h4>{t('Comptes Microsoft liés')}</h4>
              {ssoLinks.map((link) => (
                <div className="pro-person" key={link.userId}>
                  <strong>{link.name}</strong> · {link.email}
                  <button
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        await api(`/sso/links/${link.userId}`, { method: 'DELETE' });
                      })
                    }
                  >
                    {t('Révoquer la liaison')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      <h4>{t('Clés API')}</h4>
      <form
        className="pro-form"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            const result = await send('/integrations/keys', {
              name,
              scopes: keyScopes,
              expiresInDays: 30,
            });
            setOnce({ label: t('Nouvelle clé API'), secret: result.token });
            setName('');
          });
        }}
      >
        <label>
          {t('Nom de l’intégration')}
          <input
            required
            minLength={2}
            maxLength={100}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <fieldset>
          <legend>{t('Accès autorisés')}</legend>
          {scopes.map((scope) => (
            <label key={scope}>
              <input
                type="checkbox"
                checked={keyScopes.includes(scope)}
                onChange={() => choice(keyScopes, scope, setKeyScopes)}
              />
              {scope}
            </label>
          ))}
        </fieldset>
        <button disabled={busy || !keyScopes.length} type="submit">
          {t('Créer une clé valable 30 jours')}
        </button>
      </form>
      {keys.map((key) => (
        <div className="pro-person" key={key.id}>
          <strong>{key.name}</strong> · {key.scopes.join(', ')} · {t('Expire le')}{' '}
          {new Date(key.expiresAt).toLocaleDateString()}
          {key.revokedAt ? (
            <span> · {t('Révoquée')}</span>
          ) : (
            <button
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await api(`/integrations/keys/${key.id}`, { method: 'DELETE' });
                })
              }
            >
              {t('Révoquer')}
            </button>
          )}
        </div>
      ))}
      <h4>{t('Webhooks')}</h4>
      <form
        className="pro-form"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            const result = await send('/integrations/webhooks', { url, events: hookEvents });
            setOnce({ label: t('Secret de signature du webhook'), secret: result.secret });
            setUrl('');
          });
        }}
      >
        <label>
          {t('URL HTTPS publique')}
          <input
            type="url"
            required
            placeholder="https://exemple.com/vename/events"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </label>
        <fieldset>
          <legend>{t('Événements envoyés')}</legend>
          {events.map((event) => (
            <label key={event}>
              <input
                type="checkbox"
                checked={hookEvents.includes(event)}
                onChange={() => choice(hookEvents, event, setHookEvents)}
              />
              {event}
            </label>
          ))}
        </fieldset>
        <button disabled={busy || !hookEvents.length} type="submit">
          {t('Créer un webhook')}
        </button>
      </form>
      {hooks.map((hook) => (
        <div className="pro-person" key={hook.id}>
          <strong>{hook.url}</strong> · {hook.events.join(', ')} ·{' '}
          {hook.lastStatus || t('En attente')}
          <button
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await api(`/integrations/webhooks/${hook.id}`, { method: 'DELETE' });
              })
            }
          >
            {t('Supprimer')}
          </button>
        </div>
      ))}
    </div>
  );
}
